import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  readFreshSelection,
  stampSchoolSelection,
  notifySchoolSelectionUpdated,
} from '../../../../services/schoolSelectionTtl';
import { ArrowRight } from 'lucide-react';
import { Header, Footer } from '../Common';
import SecondaryBanner from '@ajat/design-system/ContentBlocks/SecondaryBanner';
import schoolShopBanner from '../../Common/SchoolShop/schoolShopBanner';
import resolveHeaderImages from '../../Common/SchoolShop/headerImages';
import FomoBlock from '@ajat/design-system/ContentBlocks/FomoBlock';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import Fullwidth from '@ajat/design-system/ContentBlocks/Fullwidth';
import Preloader from '@ajat/design-system/Preloader';
import { usePageLoader } from '@ajat/design-system/PageLoader';
import ShopEmptyState from '../../Common/SchoolShop/ShopEmptyState';
import SchoolSwitchPopup from '../../Common/SchoolShop/SchoolSwitchPopup';
import useWidget from '../../../../hooks/useWidget';
import useSchools from '../../../../hooks/useSchools';
import useSchoolClass from '../../../../hooks/useSchoolClass';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useSchoolVariations from '../../../../hooks/useSchoolVariations';
import ClosedShopMessage from '../../Common/ClosedShop/ClosedShopMessage';
import { mapProductCarouselItems } from '../../../../hooks/widgetMappers';
import { useCart } from '../../../../context/CartContext';
import productShortDescription from '../../../../utils/shortDescription';
import { pickGroup } from '../../../../services/menuDestination';
import '../../Common/SchoolShop/SchoolShop.css';
import {
  SKOLMERCH_BANNER,
  BANNER_TRUSTPILOT,
  FOMO_COUNTDOWN_TEMPLATE,
  FOMO_DATE_TEMPLATE,
  FOMO_UNITS,
  PACKAGE_DESCRIPTION,
  MORE_LABEL,
} from './schoolShopDisplay';
import { discountCardFields } from '../../../../services/discountBadge';

const SCHOOL_STORAGE_KEY = 'clsse_school_selection';
const BRAND_SLUG = 'clsse';

const variationToCardItem = (variation, schoolSlug, shopSlug, navigate) => {
  const pickColorValues = (list) => (list ?? [])
    .filter((a) => a.slug === 'color')
    .flatMap((a) => a.values.map((v) => v.rawValue ?? v.value));
  const pinnedColors = pickColorValues(variation.pinned_attributes);
  const optionColors = pickColorValues(variation.available_options);
  const imageList = Array.isArray(variation.images) ? variation.images : [];
  const firstImage = imageList[0]?.src;
  const secondImage = imageList[1]?.src;
  return {
    id: variation.id,
    image: firstImage,
    imageAlt: variation.name,
    secondImage,
    secondImageAlt: variation.name,
    title: variation.name,
    description: productShortDescription(variation),
    price: variation.price,
    ...discountCardFields(variation, 'cls-se'),
    colors: pinnedColors.length ? pinnedColors : optionColors,
    // Open the variation (with its shop/class overrides applied), not the bare
    // product, so the configurator prices match the school shop. Carry the shop
    // slug so the exact shop's overrides resolve.
    onClick: () => navigate(
      `/clothing/cls-se/school-shop/${schoolSlug}/product/${variation.id}`
      + (shopSlug ? `?shop=${encodeURIComponent(shopSlug)}` : '')
    ),
  };
};

// A package card for the bottom "Packages" grid: the package's image, name and
// price. Clicking opens the package configurator (which reads the school/shop
// it should price against from the persisted school selection).
const packageToCardItem = (pkg, schoolSlug, shopSlug, navigate) => ({
  id: `pkg-${pkg.id}`,
  image: pkg.image || undefined,
  imageAlt: pkg.name,
  title: pkg.name,
  description: productShortDescription(pkg) ?? PACKAGE_DESCRIPTION,
  price: pkg.price,
  onClick: () => navigate(
    schoolSlug
      ? `/clothing/cls-se/school-shop/${schoolSlug}/product-package/${encodeURIComponent(pkg.slug)}`
        + (shopSlug ? `?shop=${encodeURIComponent(shopSlug)}` : '')
      : `/clothing/cls-se/product-package/${encodeURIComponent(pkg.slug)}`
  ),
});

// A "shop selector" card: the shop's name + a representative product image,
// no price or colors. Clicking opens that shop's combined product list.
const shopToCardItem = (shop, schoolSlug, navigate) => ({
  id: `shop-${shop.id}`,
  image: shop.image || undefined,
  imageAlt: shop.name,
  title: shop.name,
  onClick: () => navigate(`/clothing/cls-se/school-shop/${schoolSlug}/${encodeURIComponent(shop.slug)}`),
});

const SchoolShop = () => {
  const navigate = useNavigate();
  const { schoolSlug, shopSlug, groupSlug } = useParams();
  const { schools } = useSchools('clsse');
  const { setSchool: setCartSchool, school: cartSchool, clearCart, itemCount } = useCart(BRAND_SLUG);
  // Cross-school guard: landing here with items from ANOTHER school in the
  // cart requires an explicit "erase & continue" before anything is touched.
  const [schoolConflict, setSchoolConflict] = useState(null);
  const { data: productListWidget, loading: productListLoading } = useWidget('clsse', 'product-list-1');
  const { data: packagesWidget, loading: packagesLoading } = useWidget('clsse', 'packages-1');
  const productListItems = mapProductCarouselItems(productListWidget, {
    navigate,
    productBasePath: '/clothing/cls-se/product-details',
    packageBasePath: '/clothing/cls-se/product-package',
    schoolStorageKey: SCHOOL_STORAGE_KEY,
  });
  const packageItems = mapProductCarouselItems(packagesWidget, {
    navigate,
    productBasePath: '/clothing/cls-se/product-details',
    packageBasePath: '/clothing/cls-se/product-package',
    schoolStorageKey: SCHOOL_STORAGE_KEY,
  });

  const className = useSchoolClass(BRAND_SLUG);
  const { school, shop: currentShop, group: currentGroup, menuItemImages, variations, packages: schoolPackages, shops, layout, nextShopClosesAt, nextShopClosesAtDisplay, shopClosed, loading: variationsLoading, error: variationsError } =
    useSchoolVariations(schoolSlug ? BRAND_SLUG : null, schoolSlug, className, shopSlug, groupSlug);
  // First load: the brand loader covers header + footer until the grid is ready
  // (later refetches — class / group switch — use the inline Preloader below).
  usePageLoader(Boolean(schoolSlug) && variationsLoading, { brand: 'CLS', label: 'Laddar produkter…' });
  // School/shop-scoped packages take precedence over the global widget on the
  // school shop pages; fall back to the global widget on the brand landing.
  const schoolPackageItems = useMemo(
    () => (schoolPackages || []).map((p) => packageToCardItem(p, schoolSlug, shopSlug, navigate)),
    [schoolPackages, schoolSlug, shopSlug, navigate],
  );
  const bottomPackageItems = schoolSlug ? schoolPackageItems : packageItems;
  const schoolItems = useMemo(
    () => variations.map((v) => variationToCardItem(v, schoolSlug, shopSlug, navigate)),
    [variations, schoolSlug, shopSlug, navigate],
  );
  // Shop-selector mode: only on the whole-school root view (no shop chosen yet).
  const isShopSelector = !shopSlug && layout === 'shop_selector';
  const shopSelectorItems = useMemo(
    () => shops.map((s) => shopToCardItem(s, schoolSlug, navigate)),
    [shops, schoolSlug, navigate],
  );
  const gridItems = isShopSelector ? shopSelectorItems : schoolItems;


  /* School-root URL + exactly ONE open shop → hop to the shop-slugged URL
   * (replace:true). The cart then binds that shop, so shop-scoped context —
   * e.g. the invoice-fee override shown in checkout — matches what checkout
   * charges, and both URL forms behave identically. Multi-shop schools keep
   * the root view. The query string (?class= links) is preserved. */
  useEffect(() => {
    if (!schoolSlug || shopSlug || !schools?.length) return;
    const match = schools.find((s) => s.slug === schoolSlug);
    const openShops = match?.seasons || [];
    if (openShops.length === 1 && openShops[0]?.slug) {
      // Straight into the shop's first group when it has groups (the root is
      // never a link target on a grouped shop), else the shop root.
      const entryGroup = pickGroup(openShops[0]);
      navigate(
        `/clothing/cls-se/school-shop/${schoolSlug}/${encodeURIComponent(openShops[0].slug)}${entryGroup ? `/${encodeURIComponent(entryGroup.slug)}` : ''}${window.location.search}`,
        { replace: true },
      );
    }
  }, [schoolSlug, shopSlug, schools, navigate]);

  /* Auto-persist school selection from URL slug and keep the BE cart in
   * sync — the cart's school (and, when on a shop-specific URL, the shop) is
   * what ends up on the order. */
  useEffect(() => {
    if (!schoolSlug || !schools?.length) return;
    const match = schools.find((s) => s.slug === schoolSlug);
    if (!match) return;
    // Resolve the shop id from the URL shop slug against this school's shops.
    const matchedShop = shopSlug
      ? (match.seasons || []).find((s) => s.slug === shopSlug)
      : null;
    let prev = null;
    try { prev = JSON.parse(localStorage.getItem(SCHOOL_STORAGE_KEY) || 'null'); } catch { prev = null; }
    const storedClass = prev?.school?.id === match.id ? (prev?.class ?? null) : null;
    // Only re-bind the cart when the target school or class actually differs
    // from what the cart already holds. A bare remount (e.g. returning from a
    // product page to the SAME shop) must NOT re-issue setSchool: the BE can
    // treat an identical re-bind as a change under some timing (schools not yet
    // loaded → shop resolves null) and rotate the cart to a fresh empty one,
    // losing items. Mirrors ABC-SE's school-sync gating.
    // Coerce ids before comparing: the schools API serializes ids as STRINGS
    // while the cart API returns NUMBERS — a strict mismatch here made every
    // shop visit look like a school change once the cart had a school bound,
    // so clearCart() wiped the items on returning from a product page.
    const cartSchoolId = cartSchool?.id != null ? String(cartSchool.id) : null;
    const urlSchoolId = String(match.id);
    const isSchoolChange = cartSchoolId != null && cartSchoolId !== urlSchoolId;
    // Different school + items in the cart → don't touch anything (storage or
    // cart) until the customer confirms via the SchoolSwitchPopup.
    if (isSchoolChange && itemCount > 0) {
      setSchoolConflict((cur) => cur ?? { match, matchedShop, storedClass });
      return;
    }
    if (prev?.school?.id !== match.id) {
      try {
        // Stamped, or the very next readFreshSelection() treats it as a legacy
        // entry and deletes it; notified so same-tab readers re-read.
        localStorage.setItem(SCHOOL_STORAGE_KEY, JSON.stringify(stampSchoolSelection({
          school: {
            id: match.id,
            slug: match.slug,
            name: match.name,
            classes: match.classes,
            seasons: match.seasons,
          },
          season: matchedShop ? { id: matchedShop.id, slug: matchedShop.slug, name: matchedShop.name } : null,
          class: null,
        })));
        notifySchoolSelectionUpdated(SCHOOL_STORAGE_KEY);
      } catch { /* ignore */ }
    }
    const classChanged = (cartSchool?.class ?? null) !== (storedClass ?? null);
    if (cartSchoolId !== urlSchoolId || classChanged) {
      (async () => {
        if (isSchoolChange) {
          try { await clearCart(); } catch { /* best-effort */ }
        }
        try {
          await setCartSchool({
            school_id: match.id,
            shop_id: matchedShop?.id ? Number(matchedShop.id) : null,
            school_class: storedClass,
          });
        } catch { /* cart sync is best-effort */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolSlug, shopSlug, schools, cartSchool?.id, cartSchool?.class, itemCount]);

  /* A shop link WITHOUT a class: class overrides only apply once a class is
   * chosen, so brands with the school-login popup force the choice on landing
   * (selector opens with the school preselected; requireClass makes the class
   * mandatory). Once per page visit. */
  const { showSchoolSelector, deadlineDisplay } = useBrandSettings(BRAND_SLUG);
  const promptedClassRef = useRef(false);
  useEffect(() => {
    if (!showSchoolSelector || promptedClassRef.current) return;
    if (!schoolSlug || !schools?.length) return;
    if (schoolConflict) return;
    if (className) return;
    const match = schools.find((s) => s.slug === schoolSlug);
    if (!match) return;
    const classes = shopSlug
      ? ((match.seasons || []).find((s) => s.slug === shopSlug)?.classes || [])
      : (match.seasons || []).flatMap((s) => s.classes || []);
    if (!classes.length) return; // class-less shop — nothing to force
    promptedClassRef.current = true;
    document.dispatchEvent(new CustomEvent('open-school-selector'));
  }, [showSchoolSelector, schoolSlug, shopSlug, schools, className, schoolConflict]);

  /* Cross-school guard resolution: "Back" returns to the shop the cart
   * belongs to; the red confirm erases the cart and continues here. */
  const handleSwitchBack = useCallback(() => {
    setSchoolConflict(null);
    navigate(cartSchool?.slug
      ? `/clothing/cls-se/school-shop/${cartSchool.slug}`
      : '/clothing/cls-se');
  }, [navigate, cartSchool?.slug]);

  const handleSwitchConfirm = useCallback(async () => {
    if (!schoolConflict) return;
    const { match, matchedShop, storedClass } = schoolConflict;
    try {
      localStorage.setItem(SCHOOL_STORAGE_KEY, JSON.stringify(stampSchoolSelection({
        school: {
          id: match.id,
          slug: match.slug,
          name: match.name,
          classes: match.classes,
          seasons: match.seasons,
        },
        season: matchedShop ? { id: matchedShop.id, slug: matchedShop.slug, name: matchedShop.name } : null,
        class: null,
      })));
      notifySchoolSelectionUpdated(SCHOOL_STORAGE_KEY);
    } catch { /* ignore */ }
    try { await clearCart(); } catch { /* best-effort */ }
    try {
      await setCartSchool({
        school_id: match.id,
        shop_id: matchedShop?.id ? Number(matchedShop.id) : null,
        school_class: storedClass,
      });
    } catch { /* cart sync is best-effort */ }
    setSchoolConflict(null);
  }, [schoolConflict, clearCart, setCartSchool]);

  /* Set global brand on mount, restore previous on unmount */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'CLS');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  // Redundant bare /school-shop → stored school's shop, else brand landing.
  if (!schoolSlug) {
    const sel = readFreshSelection(SCHOOL_STORAGE_KEY);
    const storedSchoolSlug = sel?.school?.slug;
    const storedShopSlug = sel?.season?.slug;
    return storedSchoolSlug
      ? <Navigate replace to={`/clothing/cls-se/school-shop/${storedSchoolSlug}${storedShopSlug ? `/${storedShopSlug}` : ''}`} />
      : <Navigate replace to="/clothing/cls-se" />;
  }

  // The requested shop's date window has passed (BE `shopClosed` marker):
  // the direct URL renders the localized closed page instead of products.
  // Unknown / removed group slug (BE 404 on ?group=): fall back to the shop
  // root rather than showing an error page for a stale link.
  if (groupSlug && shopSlug && schoolSlug && !variationsLoading && variationsError) {
    return <Navigate replace to={`/clothing/cls-se/school-shop/${schoolSlug}/${shopSlug}`} />;
  }

  if (shopClosed) {
    return (
      <>
        <Header />
        <ClosedShopMessage brandSlug="clsse" feSlug="cls-se" standalone />
        <Footer />
      </>
    );
  }

  const { tag: bannerTag, title: bannerTitle, menuItemKey: bannerMenuKey } = schoolShopBanner({
    schools, schoolSlug, shopSlug, currentShop, currentGroup, school, className,
  });
  // Brand-uploaded header photo per menu item (Overalls & More / Skolmerch),
  // else Skolmerch for every shop.
  const bannerImages = resolveHeaderImages({
    menuItemImages, menuItemKey: bannerMenuKey,
    fallback: { menu_1: SKOLMERCH_BANNER, menu_2: SKOLMERCH_BANNER },
  });

  return (
    <div className="school-shop">
      <Header />
      
      <SecondaryBanner
        tag={bannerTag}
        title={bannerTitle}
        {...(Object.keys(bannerImages).length ? bannerImages : SKOLMERCH_BANNER)}
        trustpilot={BANNER_TRUSTPILOT}
      />

      <main className="school-shop__wrapper">
        {deadlineDisplay !== 'none' && nextShopClosesAt && (
          <div className="school-shop__fomo-container">
            <FomoBlock
              closesAt={nextShopClosesAt}
              template={deadlineDisplay === 'date' ? FOMO_DATE_TEMPLATE : FOMO_COUNTDOWN_TEMPLATE}
              units={FOMO_UNITS}
              staticTime={deadlineDisplay === 'date' ? (nextShopClosesAtDisplay || undefined) : undefined}
            />
          </div>
        )}

        {/* ── Products Widget 1: Our Collection ── */}
        {schoolSlug ? (
          (variationsLoading || variationsError || gridItems.length === 0) ? (
            variationsLoading ? (
              <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <Preloader label="Laddar produkter…" />
              </div>
            ) : variationsError ? (
              <ShopEmptyState
                variant="error"
                feSlug="cls-se"
                title="Produkterna kunde inte laddas"
                text="Prova att ladda om sidan. Om problemet kvarstår hjälper vi gärna till — se vår FAQ eller kontakta oss direkt."
                contactLabel="Kontakta oss"
                faqLabel="Se FAQ"
              />
            ) : (
              <ShopEmptyState
                feSlug="cls-se"
                title="Inga produkter just nu"
                text="Butiken kanske inte har öppnat ännu, eller så uppdateras produkterna. Har du frågor hjälper vi gärna till."
                contactLabel="Kontakta oss"
                faqLabel="Se FAQ"
              />
            )
          ) : (
            <ProductsGrid
              moreLabel={MORE_LABEL}
              paddingTop="medium-l"
              paddingBottom="medium-s"
              background="var(--color-gray-0)"
              ctaIcon={<ArrowRight size={20} />}
              ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
              className={isShopSelector ? 'school-shop__shop-cards' : undefined}
              items={gridItems}
            />
          )
        ) : (
          !productListLoading && productListItems && (
            <ProductsGrid
              moreLabel={MORE_LABEL}
              paddingTop="medium-l"
              paddingBottom="medium-s"
              background="var(--color-gray-0)"
              ctaIcon={<ArrowRight size={20} />}
              ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
              items={productListItems}
            />
          )
        )}

        {/* ── Fullwidth Content Widget 1 (hidden in shop-selector mode) ── */}
        {!isShopSelector && (
        <Fullwidth
          showImage={false}
          subtitle="50.000+ ELEVER VÄLJER OSS VARJE ÅR"
          mainCopyVariant="h2"
          mainCopy="Sveriges skarpaste priser med prisgaranti, snabb leverans och attraktiva paketlösningar."
          additionalCopy="Beställ tryggt och säkert med över 100 års erfarenhet - och."
          // tags={['A4 rygtryk', '10 X 10 cm brystlogo', 'Gratis fragt', 'Nem individuel betalning', 'Frit valg af produkter']}
          showHyperlink={false}
        />
        )}

        {/* ── Products Widget 2: Packages (hidden in shop-selector mode) ── */}
        {!isShopSelector && (schoolSlug ? !variationsLoading : !packagesLoading) && bottomPackageItems && bottomPackageItems.length > 0 && (
        <ProductsGrid
          moreLabel={MORE_LABEL}
          background="var(--color-gray-0)"
          paddingTop="medium-m"
          paddingBottom="large-l"
          ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
          items={bottomPackageItems}
          // items={[
          //   { id: 13, image: require('../../../../assets/cls-se/landing/products/package-1.jpg'), imageAlt: 'STANDARD PAKET 1 (FRUIT OF THE LOOM)', title: 'STANDARD PAKET 1 (FRUIT OF THE LOOM)', description: 'Paketlösning', price: '399,00 KR', colors: ['#d9d9d9', '#080c0a', '#ffffff', '#eb4034', '#c98bc9', '#8cb1d9', '#f3cf8c', '#7db580', '#3a3a3a'], onClick: () => navigate('/clothing/cls-se/product-package') },
          //   { id: 14, image: require('../../../../assets/cls-se/landing/products/package-2.jpg'), imageAlt: 'ROYAL PAKET 2', title: 'ROYAL PAKET 2', description: 'Paketlösning', price: '389,00 KR', colors: ['#c8c1ba', '#080c0a', '#2e2e52', '#293a35', '#ffffff', '#e3e3df', '#ebbdd2', '#6b1d2e', '#4f3a3a'], onClick: () => navigate('/clothing/cls-se/product-package') },
          //   { id: 15, image: require('../../../../assets/cls-se/landing/products/package-3.jpg'), imageAlt: 'ROYAL PAKET 3', title: 'ROYAL PAKET 3', description: 'Paketlösning', price: '449,00 KR', colors: ['#6b1d2e', '#080c0a', '#2e2e52', '#293a35', '#ffffff', '#e3e3df', '#ebbdd2', '#4f3a3a', '#8cb1d9'], onClick: () => navigate('/clothing/cls-se/product-package') },
          //   { id: 16, image: require('../../../../assets/cls-se/landing/products/package-4.jpg'), imageAlt: 'ROYAL PAKET 4', title: 'ROYAL PAKET 4', description: 'Paketlösning', price: '499,00 KR', colors: ['#293a35', '#080c0a', '#2e2e52', '#ffffff', '#e3e3df', '#ebbdd2', '#6b1d2e', '#4f3a3a', '#c8c1ba'], onClick: () => navigate('/clothing/cls-se/product-package') },
          //   { id: 13, image: require('../../../../assets/cls-se/landing/products/package-1.jpg'), imageAlt: 'STANDARD PAKET 1 (FRUIT OF THE LOOM)', title: 'STANDARD PAKET 1 (FRUIT OF THE LOOM)', description: 'Paketlösning', price: '399,00 KR', colors: ['#d9d9d9', '#080c0a', '#ffffff', '#eb4034', '#c98bc9', '#8cb1d9', '#f3cf8c', '#7db580', '#3a3a3a'], onClick: () => navigate('/clothing/cls-se/product-package') },
          //   { id: 14, image: require('../../../../assets/cls-se/landing/products/package-2.jpg'), imageAlt: 'ROYAL PAKET 2', title: 'ROYAL PAKET 2', description: 'Paketlösning', price: '389,00 KR', colors: ['#c8c1ba', '#080c0a', '#2e2e52', '#293a35', '#ffffff', '#e3e3df', '#ebbdd2', '#6b1d2e', '#4f3a3a'], onClick: () => navigate('/clothing/cls-se/product-package') },
          //   { id: 15, image: require('../../../../assets/cls-se/landing/products/package-3.jpg'), imageAlt: 'ROYAL PAKET 3', title: 'ROYAL PAKET 3', description: 'Paketlösning', price: '449,00 KR', colors: ['#6b1d2e', '#080c0a', '#2e2e52', '#293a35', '#ffffff', '#e3e3df', '#ebbdd2', '#4f3a3a', '#8cb1d9'], onClick: () => navigate('/clothing/cls-se/product-package') },
          //   { id: 16, image: require('../../../../assets/cls-se/landing/products/package-4.jpg'), imageAlt: 'ROYAL PAKET 4', title: 'ROYAL PAKET 4', description: 'Paketlösning', price: '499,00 KR', colors: ['#293a35', '#080c0a', '#2e2e52', '#ffffff', '#e3e3df', '#ebbdd2', '#6b1d2e', '#4f3a3a', '#c8c1ba'], onClick: () => navigate('/clothing/cls-se/product-package') },
          // ]}
        />
        )}
      </main>

      <SchoolSwitchPopup
        isOpen={!!schoolConflict}
        brandSlug={BRAND_SLUG}
        onBack={handleSwitchBack}
        onConfirm={handleSwitchConfirm}
      />

      <Footer />
    </div>
  );
};

export default SchoolShop;
